import type { AtelierAppearance, AtelierColors, AtelierDye } from '../../shared/atelier';

export const COAT_PALETTES:Record<string,[string,string,string]>={
  ginger:['#5a2a10','#d9822b','#f6dcae'],grey:['#2b2b30','#8a8a90','#ebe7df'],
  black:['#0b0b12','#2a2c3b','#d6d4dc'],ermine:['#7a736c','#e6dfd2','#ffffff'],
  ink:['#101d58','#4a6cc0','#eef0f6'],choc:['#2a1608','#6e4223','#d9b48a'],
  silver:['#35424f','#a7b1b6','#f1ede3'],copper:['#6a3023','#bd704b','#efcfad'],cream:['#9c724a','#e7c58f','#fff4da'],
  sable:['#342e2a','#82705b','#dcc6a2'],rose:['#663d4d','#bc8591','#f0d8d4'],sage:['#39463b','#899b78','#e1dfbd'],
};
const NATURAL_PALETTES:Record<string,[string,string,string]>={
  'tabby-st':COAT_PALETTES.ginger,'ink-st':COAT_PALETTES.black,'tabby-71s':['#675548','#bba68a','#efe3cf'],
  'fox-st':['#612a1a','#c96a36','#eee2c6'],'hare-st':['#69574b','#ae9275','#e9ded0'],
  'wolf-st':COAT_PALETTES.silver,'lynx-st':COAT_PALETTES.sable,
};
/** The same pigments feed the standalone fitting and the in-game actor. */
export function resolveCoat(appearance:Pick<AtelierAppearance,'model'|'coat'|'colors'|'coatPattern'>):[string,string,string]|null {
  const colors=appearance.colors;
  if(appearance.coat==='orig'&&(!appearance.coatPattern||appearance.coatPattern==='source')&&!colors?.fur&&!colors?.markings&&!colors?.muzzle)return null;
  const base=COAT_PALETTES[appearance.coat]??NATURAL_PALETTES[appearance.model]??COAT_PALETTES.ginger;
  return [colors?.markings??base[0],colors?.fur??base[1],colors?.muzzle??base[2]];
}

export const FABRIC_PALETTES = {
  original: { base:'#3f6b45', trim:'#c9a13b', secondary:'#9e2a1c' },
  crimson: { base:'#873f42', trim:'#d9bf85', secondary:'#304a68' },
  lapis: { base:'#334f79', trim:'#dbc28b', secondary:'#95524f' },
  forest: { base:'#3e6350', trim:'#ddc99e', secondary:'#b38e50' },
  ochre: { base:'#b5853f', trim:'#efdfb3', secondary:'#37546b' },
  plum: { base:'#654361', trim:'#debe81', secondary:'#a47d4b' },
  ash: { base:'#64747b', trim:'#e2d4bd', secondary:'#354554' },
} satisfies Record<AtelierDye,{base:string;trim:string;secondary:string}>;

/** Initial colour-picker swatches match the authored outfit, rather than a generic green swatch. */
export function originalFabric(outfit:string):{base:string;trim:string;secondary:string;leather:string;metal:string}{
  const common={leather:'#5a3a1e',metal:'#9aa3ad'};
  const palettes:Record<string,{base:string;trim:string;secondary:string}>={
    crusader:{base:'#efe6d2',trim:'#9c7a22',secondary:'#9e2a1c'},monk:{base:'#6b4a2e',trim:'#4a3220',secondary:'#5e4027'},
    king:{base:'#24418f',trim:'#c9a13b',secondary:'#982f29'},bishop:{base:'#f2ece0',trim:'#c9a13b',secondary:'#c08a1e'},
    wanderer:{base:'#3f6b45',trim:'#2b1d14',secondary:'#2f5a37'},jester:{base:'#b3261e',trim:'#c9a13b',secondary:'#24418f'},
  };
  return {...common,...(palettes[outfit]??FABRIC_PALETTES.original)};
}

interface Material { base:string;trim:string;accent:string;pattern:number }
const shade=(hex:string,k:number)=>'#'+[1,3,5].map(i=>Math.round(parseInt(hex.slice(i,i+2),16)*k).toString(16).padStart(2,'0')).join('');

/** Dye fabric only: preserve the material identity of leather, rope, metal and ermine. */
export function dyeClothStyle<T extends Material>(style:T,pieceId:string,dye:string='original',colors?:AtelierColors):T {
  const custom=colors??{};
  if(pieceId.startsWith('charm-')||pieceId.includes('-crystal-')||pieceId.includes('-gold-'))return style;
  if(pieceId.startsWith('weapon-')&&pieceId.includes('-grip-'))return custom.leather?{...style,base:custom.leather}:style;
  if(pieceId.startsWith('boots-')&&style.pattern!==4)return custom.leather?{...style,base:custom.leather}:style;
  if([4,5].includes(style.pattern))return custom.metal?{...style,base:custom.metal}:style;
  if(/^belt(-|$)/.test(pieceId))return custom.leather?{...style,base:custom.leather}:style;
  if([6,8].includes(style.pattern)||/^(rope|ruff)(-|$)/.test(pieceId))return style;
  if(dye==='original'||!Object.hasOwn(FABRIC_PALETTES,dye)){
    const outer=/^(cape|cope|mantle|hood|cowl)(-|$)/.test(pieceId);
    const reversed=style.pattern===3&&/^(cap|hose)(-|$)/.test(pieceId);
    const base=(outer||reversed?custom.secondary:custom.fabric)??style.base;
    const trim=style.pattern===7?style.trim:custom.trim??style.trim;
    const accent=(style.pattern===3?(reversed?custom.fabric:custom.secondary):custom.trim)??style.accent;
    return base===style.base&&trim===style.trim&&accent===style.accent?style:{...style,base,trim,accent};
  }
  const palette=FABRIC_PALETTES[dye as AtelierDye];
  const main=custom.fabric??palette.base,second=custom.secondary??palette.secondary,trim=custom.trim??palette.trim;
  const reversed=style.pattern===3&&/^(cap|hose)(-|$)/.test(pieceId);
  let base=reversed?second:main;
  if(/^(cape|cope|mantle)(-|$)/.test(pieceId))base=second;
  if(/^(hood|cowl)(-|$)/.test(pieceId))base=custom.secondary??shade(base,.86);
  return {...style,base,trim:style.pattern===7?style.trim:trim,accent:style.pattern===3?(reversed?main:second):trim};
}
